// src/components/resources/tools/generators/BuyerPersona/personaUtils.tsx

import type { ReactNode } from 'react'
import type { PersonaData } from './PersonaContext'
import { buyerPersonaGenerator } from '~/lib/resources/data/generators/buyerPersonaGenerator'
import { 
  // EmailIcon, 
  // PhoneIcon, 
  // JobRoleIcon, 
  // DecisionAuthorityIcon, 
  // BrandLoyaltyCheckmarkIcon, 
  // BrandLoyaltyHeartIcon, 
  // BudgetIcon, 
  // KPIIcon, 
  // BudgetAllocationIcon,
  // TestVariablesIcon,
  // SuccessMetricsIcon,
  // OptimizationStrategiesIcon,
  FacebookIcon,
  InstagramIcon,
  TwitterIcon,
  LinkedInIcon,
  TikTokIcon,
  PinterestIcon,
  YouTubeIcon
} from '~/components/resources/tools/generators/BuyerPersona/Icons'

/**
 * Properly capitalize and format a string in title case
 */
export function properCapitalizeTitleCase(str: string): string {
  if (!str) return ''
  
  const minorWords = ['a', 'an', 'the', 'and', 'but', 'or', 'for', 'nor', 'on', 'at', 'to', 'from', 'by', 'in', 'of']
  
  return str.split(' ')
    .map((word, index) => {
      // Preserve casing for words that have mixed casing
      if (/[a-z][A-Z]/.test(word)) {
        return word
      }
      
      // Don't lowercase articles, conjunctions, and prepositions unless they're the first word
      if (index > 0 && minorWords.includes(word.toLowerCase())) {
        return word.toLowerCase()
      }
      
      // Preserve acronyms
      if (word.toUpperCase() === word && word.length > 1) {
        return word
      }
      
      // Capitalize the first letter of other words
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
    })
    .join(' ')
}

/**
 * Get icon for a specific marketing channel
 */
export function getChannelIcon(channel: string): ReactNode {
  // Icons for different marketing channels
  const channelIcons: Record<string, ReactNode> = {
    Email: (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    'Search': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
      </svg>
    ),
    'Social Media': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    ),
    'Display': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    'Video': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
    ),
    'Television': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    'In-Person': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
      </svg>
    ),
    'Live Chat': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
      </svg>
    ),
    'Phone': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
      </svg>
    ),
    'Messaging Apps': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    ),
    'Direct Mail': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 19v-8.93a2 2 0 01.89-1.664l7-4.666a2 2 0 012.22 0l7 4.666A2 2 0 0121 10.07V19M3 19a2 2 0 002 2h14a2 2 0 002-2M3 19l6.75-4.5M21 19l-6.75-4.5M3 10l6.75 4.5M21 10l-6.75 4.5m0 0l-1.14.76a2 2 0 01-2.22 0l-1.14-.76" />
      </svg>
    )
  }
  
  // Default to a generic icon if no match
  const defaultIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
    </svg>
  )
  
  // Try to find an exact match or a partial match
  const exactMatch = channelIcons[channel]
  if (exactMatch) return exactMatch
  
  // Look for partial matches (case insensitive)
  const lowerChannel = channel.toLowerCase()
  for (const [key, icon] of Object.entries(channelIcons)) {
    if (key.toLowerCase().includes(lowerChannel) || lowerChannel.includes(key.toLowerCase())) {
      return icon
    }
  }
  
  return defaultIcon
}

/**
 * Get icon for a specific platform
 */
export function getPlatformIcon(platform: string): ReactNode {
  const name = platform.toLowerCase()
  
  if (name.includes('facebook')) return <FacebookIcon />
  if (name.includes('instagram')) return <InstagramIcon />
  if (name.includes('linkedin')) return <LinkedInIcon />
  if (name.includes('twitter') || name.includes('x')) return <TwitterIcon />
  if (name.includes('youtube')) return <YouTubeIcon />
  if (name.includes('tiktok')) return <TikTokIcon />
  if (name.includes('pinterest')) return <PinterestIcon />
  
  // Default to a generic icon if no match
  return (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  )
}

/**
 * Get emoji for a specific device
 */
export function getDeviceEmoji(name: string): string {
  name = name.toLowerCase()
  if (name.includes('desktop')) return '🖥️'
  if (name.includes('laptop')) return '💻'
  if (name.includes('tablet')) return '📱'
  if (name.includes('smartphone')) return '📱'
  if (name.includes('smartwatch')) return '⌚'
  if (name.includes('smart tv')) return '📺'
  if (name.includes('voice')) return '🎤'
  return '📟'
}

/**
 * Get emoji for content type
 */
export function getContentEmoji(name: string): string {
  name = name.toLowerCase()
  if (name.includes('blog')) return '📄'
  if (name.includes('video')) return '🎬'
  if (name.includes('social')) return '👥'
  if (name.includes('podcast')) return '🎧'
  if (name.includes('infographic')) return '📊'
  if (name.includes('ebook') || name.includes('whitepaper')) return '📚'
  if (name.includes('case')) return '📋'
  if (name.includes('webinar')) return '🖥️'
  if (name.includes('email') || name.includes('newsletter')) return '📧'
  if (name.includes('tv') || name.includes('streaming')) return '📺'
  if (name.includes('review')) return '⭐'
  if (name.includes('tutorial') || name.includes('how-to')) return '📝'
  return '📄'
}

/**
 * Get emoji for marketing channel
 */
export function getChannelEmoji(name: string): string {
  name = name.toLowerCase()
  if (name.includes('email')) return '📧'
  if (name.includes('social')) return '👥'
  if (name.includes('search')) return '🔍'
  if (name.includes('ppc') || name.includes('pay per click')) return '💰'
  if (name.includes('display')) return '🖼️'
  if (name.includes('video')) return '🎬'
  if (name.includes('influencer')) return '🌟'
  if (name.includes('content')) return '📄'
  if (name.includes('event')) return '🎪'
  if (name.includes('direct')) return '📪'
  if (name.includes('pr') || name.includes('public relations')) return '📢'
  if (name.includes('affiliate')) return '🤝'
  if (name.includes('radio')) return '📻'
  if (name.includes('tv')) return '📺'
  if (name.includes('print')) return '📰'
  if (name.includes('billboard')) return '🏙️'
  return '📣'
}

/**
 * Get icon for a specific device
 */
export function getDeviceIcon(device: string): ReactNode {
  // Icons for different devices
  const deviceIcons: Record<string, ReactNode> = {
    'Mobile': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    'Desktop': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    ),
    'Tablet': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
      </svg>
    ),
    'Smart TV': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
      </svg>
    )
  }
  
  // Default to a generic icon if no match
  const defaultIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  )
  
  // Try to find an exact match or a partial match
  const exactMatch = deviceIcons[device]
  if (exactMatch) return exactMatch
  
  // Look for partial matches (case insensitive)
  const lowerDevice = device.toLowerCase()
  for (const [key, icon] of Object.entries(deviceIcons)) {
    if (key.toLowerCase().includes(lowerDevice) || lowerDevice.includes(key.toLowerCase())) {
      return icon
    }
  }
  
  return defaultIcon
}

/**
 * Get icon for digital influence level
 */
export function getDigitalInfluenceIcon(influence: string): ReactNode {
  // Icons for different digital influence levels
  const influenceIcons: Record<string, ReactNode> = {
    'Influencer': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
      </svg>
    ),
    'Active Participant': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 11.5V14m0-2.5v-6a1.5 1.5 0 113 0m-3 6a1.5 1.5 0 00-3 0v2a7.5 7.5 0 0015 0v-5a1.5 1.5 0 00-3 0m-6-3V11m0-5.5v-1a1.5 1.5 0 013 0v1m0 0V11m0-5.5a1.5 1.5 0 013 0v3m0 0V11" />
      </svg>
    ),
    'Occasional Contributor': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8h2a2 2 0 012 2v6a2 2 0 01-2 2h-2v4l-4-4H9a1.994 1.994 0 01-1.414-.586m0 0L11 14h4a2 2 0 002-2V6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2v4l.586-.586z" />
      </svg>
    ),
    'Lurker': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
      </svg>
    ),
    'Minimal Digital Presence': (
      <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M18.364 18.364A9 9 0 005.636 5.636m12.728 12.728A9 9 0 015.636 5.636m12.728 12.728L5.636 5.636" />
      </svg>
    )
  }
  
  // Default to a generic icon if no match
  const defaultIcon = (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4 text-teal-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  )
  
  // Try to find an exact match or a partial match
  const exactMatch = influenceIcons[influence]
  if (exactMatch) return exactMatch
  
  // Look for partial matches (case insensitive)
  const lowerInfluence = influence.toLowerCase()
  for (const [key, icon] of Object.entries(influenceIcons)) {
    if (key.toLowerCase().includes(lowerInfluence) || lowerInfluence.includes(key.toLowerCase())) {
      return icon
    }
  }
  
  return defaultIcon
}

/**
 * Validate a step in the buyer persona generator
 */
export function validateStep(data: PersonaData, stepIndex: number): boolean {
  const steps = buyerPersonaGenerator.config.steps
  
  // If step doesn't exist, consider it valid
  if (!steps?.[stepIndex]) {
    return true
  }
  
  const step = steps[stepIndex]
  
  // No fields means no validation needed
  if (!step.fields?.length) {
    return true
  }
  
  // Get all fields that are required (either always required or conditionally)
  const requiredFields = step.fields.filter(field => {
    // Always required
    if (field.validation?.required) {
      return true
    }
    
    // Conditionally required - check if the condition applies
    if (field.validation?.requiredIf) {
      const { field: conditionField, operator, value } = field.validation.requiredIf
      const fieldValue = data[conditionField]
      
      // Basic condition evaluation
      switch (operator) {
        case 'equals':
          return fieldValue === value
        case 'notEquals':
          return fieldValue !== value
        case 'contains':
          if (Array.isArray(fieldValue)) {
            return fieldValue.includes(value as string)
          } else if (typeof fieldValue === 'string') {
            return fieldValue.includes(value as string)
          }
          return false
        case 'notContains':
          if (Array.isArray(fieldValue)) {
            return !fieldValue.includes(value as string)
          } else if (typeof fieldValue === 'string') {
            return !fieldValue.includes(value as string)
          }
          return true
        case 'greaterThan':
          return Number(fieldValue) > Number(value)
        case 'lessThan':
          return Number(fieldValue) < Number(value)
        case 'greaterThanOrEquals':
          return Number(fieldValue) >= Number(value)
        case 'lessThanOrEquals':
          return Number(fieldValue) <= Number(value)
        case 'exists':
          return !!fieldValue
        case 'notExists':
          return !fieldValue
        case 'isEmpty':
          return !fieldValue || (Array.isArray(fieldValue) && fieldValue.length === 0) || (typeof fieldValue === 'string' && fieldValue.trim() === '')
        case 'isNotEmpty':
          return !!fieldValue && !(Array.isArray(fieldValue) && fieldValue.length === 0) && !(typeof fieldValue === 'string' && fieldValue.trim() === '')
        default:
          return false
      }
    }
    
    return false
  })
  
  // If no required fields, the step is valid
  if (requiredFields.length === 0) {
    return true
  }
  
  // Check each required field
  for (const field of requiredFields) {
    const value = data[field.id]
    
    // Skip checking fields that shouldn't be visible based on conditions
    if (field.showIf) {
      // Basic check for showIf conditions - if parent field isn't filled
      const { field: conditionField, operator } = field.showIf
      const parentValue = data[conditionField]
      
      if (operator === 'exists' && !parentValue) {
        continue
      }
      
      if (operator === 'equals' && parentValue !== field.showIf.value) {
        continue
      }
    }
    
    // Field is invalid if it doesn't exist or is empty
    if (value === undefined || value === null || value === '') {
      return false
    }
    
    // For arrays, check if they have at least one item
    if (Array.isArray(value) && value.length === 0) {
      return false
    }
  }
  
  // All required fields are valid
  return true
} 